import { useAuth } from '@/src/context/AuthContext';
import { useTheme } from '@/src/context/ThemeContext';
import {
  acceptTournamentInvitationUseCase,
  cancelTournamentInvitationUseCase,
  declineTournamentInvitationUseCase,
  getInvitationsReceivedUseCase,
  getInvitationsSentUseCase,
  getTeamInvitationsUseCase,
  acceptTeamInvitationUseCase,
  declineTeamInvitationUseCase,
  cancelTeamInvitationUseCase,
} from '@/src/core/dependencies';
import { TeamInvitation, TournamentInvitationWithDetails } from '@/src/domain/models';
import { useTranslation } from '@/src/i18n';
import { InvitationsScreen } from '@/src/presentation/screens/InvitationsScreen';
import { useFocusEffect } from '@react-navigation/native';
import { useRouter } from 'expo-router';
import { useCallback, useState } from 'react';
import { platformAlert } from '@/src/core/utils/platformAlert';
import { logger } from '@/src/core/utils';

const PAGE_SIZE = 20;

export default function InvitationsContainer() {
  const router = useRouter();
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [receivedInvitations, setReceivedInvitations] = useState<TournamentInvitationWithDetails[]>([]);
  const [sentInvitations, setSentInvitations] = useState<TournamentInvitationWithDetails[]>([]);
  const [teamInvitationsReceived, setTeamInvitationsReceived] = useState<TeamInvitation[]>([]);
  const [teamInvitationsSent, setTeamInvitationsSent] = useState<TeamInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMoreReceived, setHasMoreReceived] = useState(true);
  const [hasMoreSent, setHasMoreSent] = useState(true);
  const [pageReceived, setPageReceived] = useState(0);
  const [pageSent, setPageSent] = useState(0);
  const [activeTab, setActiveTab] = useState<'received' | 'sent'>('received');
  const [typeTab, setTypeTab] = useState<'tournaments' | 'teams'>('tournaments');

  useFocusEffect(
    useCallback(() => {
      loadInvitations();
    }, [])
  );

  const loadInvitations = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setPageReceived(0);
      setPageSent(0);
      setHasMoreReceived(true);
      setHasMoreSent(true);

      const [received, sent, teamInvitations] = await Promise.all([
        getInvitationsReceivedUseCase.execute(user.id, PAGE_SIZE, 0),
        getInvitationsSentUseCase.execute(user.id, PAGE_SIZE, 0),
        getTeamInvitationsUseCase.execute(user.id),
      ]);

      setReceivedInvitations(received);
      setSentInvitations(sent);
      setHasMoreReceived(received.length === PAGE_SIZE);
      setHasMoreSent(sent.length === PAGE_SIZE);
      setTeamInvitationsReceived(teamInvitations.received);
      setTeamInvitationsSent(teamInvitations.sent);
    } catch (error) {
      logger.error('Invitations', 'Failed to load invitations', { error });
      platformAlert(t('common.error'), t('errors.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const loadMoreReceived = async () => {
    if (loadingMore || !hasMoreReceived || !user) return;

    try {
      setLoadingMore(true);
      const nextPage = pageReceived + 1;
      const data = await getInvitationsReceivedUseCase.execute(
        user.id,
        PAGE_SIZE,
        nextPage * PAGE_SIZE
      );

      if (data.length > 0) {
        setReceivedInvitations((prev) => [...prev, ...data]);
        setPageReceived(nextPage);
        setHasMoreReceived(data.length === PAGE_SIZE);
      } else {
        setHasMoreReceived(false);
      }
    } catch (error) {
      logger.error('Invitations', 'Failed to load more received invitations', { error });
    } finally {
      setLoadingMore(false);
    }
  };

  const loadMoreSent = async () => {
    if (loadingMore || !hasMoreSent || !user) return;

    try {
      setLoadingMore(true);
      const nextPage = pageSent + 1;
      const data = await getInvitationsSentUseCase.execute(user.id, PAGE_SIZE, nextPage * PAGE_SIZE);

      if (data.length > 0) {
        setSentInvitations((prev) => [...prev, ...data]);
        setPageSent(nextPage);
        setHasMoreSent(data.length === PAGE_SIZE);
      } else {
        setHasMoreSent(false);
      }
    } catch (error) {
      logger.error('Invitations', 'Failed to load more sent invitations', { error });
    } finally {
      setLoadingMore(false);
    }
  };

  const handleAccept = async (invitationId: string) => {
    if (!user) return;

    try {
      const userName = user.user_metadata?.full_name || user.user_metadata?.name || user.email?.split('@')[0] || 'Player';

      const invitation = await acceptTournamentInvitationUseCase.execute({
        invitationId,
        inviteeId: user.id,
        inviteeName: userName,
      });

      setReceivedInvitations((prev) =>
        prev.map((inv) => (inv.id === invitationId ? { ...inv, status: 'accepted' as const } : inv))
      );

      platformAlert(t('common.success'), t('invitations.invitationAccepted'));
      router.push(`/tournament/${invitation.tournamentId}`);
    } catch (error) {
      if (error instanceof Error && error.message === 'Tournament is full') {
        platformAlert(t('common.error'), error.message);
      } else {
        logger.error('Invitations', 'Failed to accept invitation', { error });
        platformAlert(t('common.error'), t('errors.failedToLoad'));
      }
    }
  };

  const handleDecline = async (invitationId: string) => {
    try {
      await declineTournamentInvitationUseCase.execute(invitationId);
      setReceivedInvitations((prev) =>
        prev.map((inv) => (inv.id === invitationId ? { ...inv, status: 'declined' as const } : inv))
      );
      platformAlert(t('common.success'), t('invitations.invitationDeclined'));
    } catch (error) {
      logger.error('Invitations', 'Failed to decline invitation', { error });
      platformAlert(t('common.error'), t('errors.failedToLoad'));
    }
  };

  const handleCancel = async (invitationId: string) => {
    try {
      await cancelTournamentInvitationUseCase.execute(invitationId);
      setSentInvitations((prev) =>
        prev.map((inv) => (inv.id === invitationId ? { ...inv, status: 'cancelled' as const } : inv))
      );
      platformAlert(t('common.success'), t('invitations.invitationCancelled'));
    } catch (error) {
      logger.error('Invitations', 'Failed to cancel invitation', { error });
      platformAlert(t('common.error'), t('errors.failedToLoad'));
    }
  };

  const handleUserPress = (userId: string) => {
    router.push(`/profile/${userId}`);
  };

  const handleTournamentPress = (tournamentId: string) => {
    router.push(`/tournament/${tournamentId}`);
  };

  const handleTeamInvitationAccept = async (invitationId: string, teamId: string) => {
    if (!user) return;
    try {
      await acceptTeamInvitationUseCase.execute(invitationId, user.id, teamId);
      setTeamInvitationsReceived((prev) =>
        prev.map((inv) => (inv.id === invitationId ? { ...inv, status: 'accepted' as const } : inv)),
      );
      platformAlert(t('common.success'), t('teams.memberAdded'));
      router.push(`/team/${teamId}`);
    } catch (error) {
      logger.error('Invitations', 'Failed to accept team invitation', { error });
      platformAlert(t('common.error'), t('errors.failedToLoad'));
    }
  };

  const handleTeamInvitationDecline = async (invitationId: string) => {
    try {
      await declineTeamInvitationUseCase.execute(invitationId);
      setTeamInvitationsReceived((prev) =>
        prev.map((inv) => (inv.id === invitationId ? { ...inv, status: 'declined' as const } : inv)),
      );
    } catch (error) {
      logger.error('Invitations', 'Failed to decline team invitation', { error });
      platformAlert(t('common.error'), t('errors.failedToLoad'));
    }
  };

  const handleTeamInvitationCancel = async (invitationId: string) => {
    try {
      await cancelTeamInvitationUseCase.execute(invitationId);
      setTeamInvitationsSent((prev) =>
        prev.map((inv) => (inv.id === invitationId ? { ...inv, status: 'cancelled' as const } : inv)),
      );
    } catch (error) {
      logger.error('Invitations', 'Failed to cancel team invitation', { error });
      platformAlert(t('common.error'), t('errors.failedToLoad'));
    }
  };

  return (
    <InvitationsScreen
      receivedInvitations={receivedInvitations}
      sentInvitations={sentInvitations}
      teamInvitationsReceived={teamInvitationsReceived}
      teamInvitationsSent={teamInvitationsSent}
      loading={loading}
      loadingMore={loadingMore}
      activeTab={activeTab}
      typeTab={typeTab}
      setActiveTab={setActiveTab}
      setTypeTab={setTypeTab}
      onAccept={handleAccept}
      onDecline={handleDecline}
      onCancel={handleCancel}
      onTeamInvitationAccept={handleTeamInvitationAccept}
      onTeamInvitationDecline={handleTeamInvitationDecline}
      onTeamInvitationCancel={handleTeamInvitationCancel}
      onUserPress={handleUserPress}
      onTournamentPress={handleTournamentPress}
      onTeamPress={(teamId) => router.push(`/team/${teamId}`)}
      onRefresh={loadInvitations}
      onLoadMoreReceived={loadMoreReceived}
      onLoadMoreSent={loadMoreSent}
      colors={colors}
    />
  );
}
